import React, { useMemo } from 'react';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { ErrorMsg } from '@app/form';
import { useTranslation } from 'react-i18next';
import { QuotaInput } from './QuotaInput';
import { NewQuota } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.grayBackground,
    border: 'none',
    borderRadius: theme.borderRadius.std,
  },
  inputList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  warning: {
    marginTop: theme.marginBase / 2,
  },
}));

interface Props {
  quotas: NewQuota[];
  options: { label: string; value: string }[];
  numberOfParticipantRequired: number;
  onChange: (quotas: NewQuota[]) => void;
}

export const QuotaInputList = ({
  quotas,
  options,
  numberOfParticipantRequired,
  onChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const optionsMap = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((option) => {
      map.set(option.value, option.label);
    });
    return map;
  }, [options]);

  const handleChange = (index: number, newValue: number) => {
    const updated = [...quotas];
    updated[index] = { ...updated[index], max: newValue };
    onChange(updated);
  };

  const sum = quotas.reduce(
    (acc, quota) => acc + (quota.max ?? quota.value ?? 0),
    0,
  );
  const isInvalidMessage =
    sum !== numberOfParticipantRequired
      ? sum > numberOfParticipantRequired
        ? t('editSession.targeting.quotas.overly', {
            count: sum,
            required: numberOfParticipantRequired,
          })
        : t('editSession.targeting.quotas.insufficient', {
            count: sum,
            required: numberOfParticipantRequired,
          })
      : false;

  return (
    <div className={classes.container}>
      <div className={classes.inputList}>
        {quotas.map((quota: NewQuota, index) => (
          <QuotaInput
            key={quota.item as string}
            value={quota.max ?? quota.value ?? 0}
            label={
              optionsMap.get(quota.item as string) ?? quota.item.toString()
            }
            onChange={(newValue) => handleChange(index, newValue)}
          />
        ))}
      </div>
      {!!isInvalidMessage && (
        <ErrorMsg
          className={classes.warning}
          variant="warn"
          error={isInvalidMessage}
        />
      )}
    </div>
  );
};
